import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const fetchWalletTransactions = createAsyncThunk(
  "wallet/fetchWalletTransactions",
  async ({ page = 1, limit = 10 } = {}, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;

      if (!token) return rejectWithValue("Please login first.");
      // userId optional as per API, but you want user-based results:
      if (!userId) return rejectWithValue("UserId not found in token.");

      const params = new URLSearchParams();
      params.set("userId", userId); // remove this if you want all transactions
      params.set("limit", String(limit));
      params.set("page", String(page));

      const url =
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Wallet" +
        `?${params.toString()}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: token, // if backend expects Bearer: `Bearer ${token}`
        },
      });

      const rawText = await res.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }

      if (!res.ok) {
        const msg =
          data?.message || data?.error || `Request failed (${res.status})`;
        return rejectWithValue(msg);
      }

      // normalize list
      const d = data?.data ?? data;
      const listCandidate =
        d?.transactions ??
        d?.items ??
        d?.records ??
        d?.history ??
        d?.walletTransactions ??
        d?.result ??
        d?.data ??
        (Array.isArray(d) ? d : []);

      const items = Array.isArray(listCandidate) ? listCandidate : [];

      // normalize meta
      const metaPage = Number(d?.page ?? data?.page ?? page ?? 1);
      const metaLimit = Number(d?.limit ?? data?.limit ?? limit ?? 10);
      const total =
        Number(
          d?.total ??
            d?.count ??
            data?.total ??
            data?.count ??
            items.length ??
            0,
        ) || 0;
      const totalPages =
        Number(d?.totalPages ?? d?.pages ?? data?.totalPages ?? data?.pages) ||
        Math.max(1, Math.ceil(total / Math.max(1, metaLimit)));

      return {
        items,
        meta: { page: metaPage, limit: metaLimit, total, totalPages },
      };
    } catch (err) {
      return rejectWithValue(
        err?.message || "Failed to fetch wallet transactions",
      );
    }
  },
);

export const requestWithdrawal = createAsyncThunk(
  "wallet/requestWithdrawal",
  async ({ amount }, { getState, rejectWithValue }) => {
    try {
      const { token, userId } = getState().auth;

      if (!token) return rejectWithValue("Please login first.");
      if (!userId) return rejectWithValue("UserId not found.");

      const res = await fetch(
        "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod/Withdrawal",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token, // same as wallet API
            // OR if needed:
            // Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId,
            amount: Number(amount),
          }),
        },
      );

      const rawText = await res.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }

      if (!res.ok) {
        const msg =
          data?.message || data?.error || `Request failed (${res.status})`;
        return rejectWithValue(msg);
      }

      return data;
    } catch (err) {
      return rejectWithValue(err?.message || "Withdrawal failed");
    }
  },
);

const walletSlice = createSlice({
  name: "wallet",
  initialState: {
    items: [],
    status: "idle",
    withdrawStatus: "idle",
    withdrawError: "",
    withdrawSuccess: "",
    error: "",
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  reducers: {
    setWalletPage: (state, action) => {
      state.page = action.payload;
    },
    setWalletLimit: (state, action) => {
      state.limit = action.payload;
      state.page = 1;
    },
    clearWalletError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWalletTransactions.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchWalletTransactions.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload?.items || [];

        const m = action.payload?.meta || {};
        state.page = m.page ?? state.page;
        state.limit = m.limit ?? state.limit;
        state.total = m.total ?? state.total;
        state.totalPages = m.totalPages ?? state.totalPages;

        // keep page valid
        if (state.page > state.totalPages) state.page = state.totalPages;
        if (state.page < 1) state.page = 1;
      })
      .addCase(fetchWalletTransactions.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch wallet transactions";
        state.items = [];
      })
      .addCase(requestWithdrawal.pending, (state) => {
        state.withdrawStatus = "loading";
        state.withdrawError = "";
        state.withdrawSuccess = "";
      })
      .addCase(requestWithdrawal.fulfilled, (state, action) => {
        state.withdrawStatus = "succeeded";
        state.withdrawSuccess =
          action.payload?.message || "Withdrawal request submitted";
      })
      .addCase(requestWithdrawal.rejected, (state, action) => {
        state.withdrawStatus = "failed";
        state.withdrawError = action.payload || "Withdrawal failed";
      });
  },
});

export const { setWalletPage, setWalletLimit, clearWalletError } =
  walletSlice.actions;

export default walletSlice.reducer;

// selectors
export const selectWalletItems = (s) => s.wallet.items;
export const selectWalletStatus = (s) => s.wallet.status;
export const selectWalletError = (s) => s.wallet.error;
export const selectWalletMeta = (s) => ({
  page: s.wallet.page,
  limit: s.wallet.limit,
  total: s.wallet.total,
  totalPages: s.wallet.totalPages,
});
